/**
 * Journal Review Queue — Wave D-2.
 *
 * Curated list of posted journal entries that deserve a human look before
 * close: outliers, duplicates, weekend posts, round-dollar placeholders,
 * un-memo'd manual entries. Read-only — every entry here is already posted.
 * Acting on a finding is a deep-link into Journal Entries (reverse / drill).
 */
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { BooksNavRail } from "@/components/accounting/BooksNavRail";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useGeneralLedger } from "@/data/accounting/GeneralLedgerContext";
import {
  ANOMALY_LABEL,
  detectAnomalies,
  getReviewQueue,
} from "@/data/accounting/journalAnomalies";
import { explainJournalEntry } from "@/data/accounting/journalExplanations";
import { getMaterialityFloor } from "@/data/accounting/settings/accountingSettings";
import { Sparkles } from "lucide-react";

export default function JournalReviewQueuePage() {
  const { journalEntries, accounts } = useGeneralLedger();
  const queue = useMemo(
    () => getReviewQueue(journalEntries, getMaterialityFloor()),
    [journalEntries],
  );
  const all = useMemo(() => detectAnomalies(journalEntries), [journalEntries]);

  const totalPosted = all.length;
  const flagged = queue.length;
  const clean = totalPosted - flagged;
  const avgConfidence = totalPosted === 0
    ? 100
    : Math.round(all.reduce((s, i) => s + i.confidence, 0) / totalPosted);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <BooksNavRail />
      <div className="mb-6">
        <h1 data-tour-id="review-queue-list" className="text-2xl font-semibold">Journal Review Queue</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Posted entries that look unusual. Review before close — every flag is
          a question, not an error.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <SummaryTile label="Average confidence" value={`${avgConfidence}`} suffix=" / 100" tone={avgConfidence >= 90 ? "green" : avgConfidence >= 75 ? "yellow" : "red"} />
        <SummaryTile label="Flagged entries" value={`${flagged}`} tone={flagged === 0 ? "green" : flagged < 5 ? "yellow" : "red"} />
        <SummaryTile label="Clean entries" value={`${clean}`} tone="neutral" />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Needs review</CardTitle>
        </CardHeader>
        <CardContent>
          {queue.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">
              Nothing to review. Every posted entry passed the anomaly checks.
            </div>
          ) : (
            <div className="space-y-2">
              {queue.map(item => (
                <div
                  key={item.entry.id}
                  className="rounded-md border border-border bg-card p-3 flex flex-col md:flex-row md:items-start md:justify-between gap-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link
                        to="/accounting/gl/journal-entries"
                        className="text-sm font-medium text-primary hover:underline"
                      >
                        {item.entry.number}
                      </Link>
                      <span className="text-xs text-muted-foreground">
                        {item.entry.postingDate} · {item.entry.source}
                      </span>
                      <ConfidencePill value={item.confidence} />
                    </div>
                    <div className="text-sm mt-1 truncate">{item.entry.description}</div>
                    {(() => {
                      const ex = explainJournalEntry(item.entry, accounts);
                      return (
                        <div className="mt-1.5 flex items-start gap-1.5 text-xs text-muted-foreground">
                          <Sparkles className="h-3 w-3 mt-0.5 text-primary shrink-0" />
                          <span><span className="font-medium text-foreground">Why this posted:</span> {ex.explanation}</span>
                        </div>
                      );
                    })()}
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {item.flags.map(f => (
                        <Badge
                          key={f.kind}
                          variant="outline"
                          className="text-[10px] font-normal"
                          title={f.reason}
                        >
                          {ANOMALY_LABEL[f.kind]}
                        </Badge>
                      ))}
                    </div>
                    <ul className="mt-2 text-xs text-muted-foreground space-y-0.5">
                      {item.flags.map(f => (
                        <li key={f.kind}>· {f.reason}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-xs text-muted-foreground">Largest line</div>
                    <div className="text-sm font-mono">
                      ${item.magnitude.toFixed(2)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SummaryTile({
  label, value, suffix, tone,
}: {
  label: string;
  value: string;
  suffix?: string;
  tone: "green" | "yellow" | "red" | "neutral";
}) {
  const toneClass = tone === "green"
    ? "text-emerald-600"
    : tone === "yellow"
      ? "text-amber-600"
      : tone === "red"
        ? "text-rose-600"
        : "text-foreground";
  return (
    <div className="rounded-md border border-border bg-card p-4">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-2xl font-semibold mt-1 ${toneClass}`}>
        {value}<span className="text-sm text-muted-foreground">{suffix}</span>
      </div>
    </div>
  );
}

function ConfidencePill({ value }: { value: number }) {
  const tone = value >= 90
    ? "bg-emerald-500/15 text-emerald-700 border-emerald-500/30"
    : value >= 70
      ? "bg-amber-500/15 text-amber-700 border-amber-500/30"
      : "bg-rose-500/15 text-rose-700 border-rose-500/30";
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${tone}`}>
      {value} confidence
    </span>
  );
}
